import React from 'react';
import { isNil } from 'lodash';
import { Form } from '@ant-design/compatible';
import cx from 'classnames';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import Button from '../../../../../../components/Button';
import InputForm from '../../../../../../components/form/InputForm';
import { formRequiredRules } from '../../../../../../lib/formRules';
import {
  REX_ACCOUNT_HOLDER_EXCEPT_BRACKET,
  REX_ACCOUNT_NUMBER,
  REX_INVITE_FRIEND_CODE,
  REX_PHONE,
  REX_USER_NAME,
} from '../../../../../../lib/rex';
import { REQUIRED_MSG } from '../../../../../../constants/service/common/formRulesConstants';
import BaseForm from '../../../../../../components/form/BaseForm/BaseForm';
import SelectForm from '../../../../../../components/form/SelectForm';
import {
  ACCOUNT_USER_BANK_ID,
  ACCOUNT_USER_BANK_LIST,
  ACCOUNT_USER_DETAIL_STATUS,
  ACCOUNT_USER_DETAIL_STATUS_TEXT,
  ACCOUNT_USER_GENDER_TYPE,
  ACCOUNT_USER_GENDER_TYPE_TEXT,
  ACCOUNT_USER_RECEIVE_TYPE,
} from '../../../../../../constants/service/app/accountUserConstants';
import { setDateFormat } from '../../../../../../lib/moment';
import DateSingleForm from '../../../../../../components/form/DateSingleForm';
import {
  DATE_FORMAT,
  DATE_PICKER_DEFAULT_PLACEHOLDER_TEXT,
} from '../../../../../../constants/service/common/utilConstants';
import { MAXIMUM_NUMBER_OF_BALANCE_ACCOUNT_ACCOUNT_HOLDER_TEXT } from '../../../../../../constants/service/app/balanceConstants';
import EditAccountUserStatusModal from '../../common/modal/EditAccountUserStatusModal';
import { AccountUserDetailTypeV2, EditableUserDetailType } from '../../common/types/accountUserDetail';
import { useUserInfoEditSectionViewModel } from './UserInfoEditSectionViewModel';

export interface IUserInfoEditSectionProps extends FormComponentProps {
  userId: number;
  isActive: boolean;
  userDetailInfo: AccountUserDetailTypeV2;
}

const UserInfoEditSection = (props: IUserInfoEditSectionProps): JSX.Element => {
  const { form, userDetailInfo } = props;
  const {
    editableUserInfo,
    isEditable,
    isOpenEditAccountUserStatusModal,
    handleInputForm,
    handleEdit,
    handleCancelEdit,
    handleOpenAccountUserStatusModal,
    handleCloseAccountUserStatusModal,
    handleSave,
  } = useUserInfoEditSectionViewModel(props);

  return (
    <div className="account-user-detail-section">
      <div className="title-header-section">
        <div className="title">유저 기본 정보</div>
        <div className="buttons">
          {isEditable ? (
            <>
              <Button color="primary" text="저장하기" onClick={handleOpenAccountUserStatusModal} />
              <Button text="취소하기" onClick={handleCancelEdit} />
            </>
          ) : (
            <Button color="primary" text="편집하기" onClick={handleEdit} />
          )}
        </div>
      </div>

      <div className="account-user-detail-section-content">
        <div className="form-wrap">
          <InputForm
            className="user-detail-input-form"
            label="이름"
            form={form}
            isEditable={isEditable}
            rules={[formRequiredRules(false, null, false), { pattern: REX_USER_NAME, message: '이름을 확인해주세요!' }]}
            initialValue={editableUserInfo.name}
            name="name"
          />

          <InputForm
            className="user-detail-input-form"
            label="핸드폰"
            form={form}
            isEditable={isEditable}
            rules={[
              formRequiredRules(true, REQUIRED_MSG),
              { pattern: REX_PHONE, message: '핸드폰 번호를 확인해주세요' },
            ]}
            initialValue={editableUserInfo.cellPhone}
            name="cellPhone"
          />
        </div>

        <div className="form-wrap">
          <InputForm
            className="user-detail-input-form"
            label="이메일"
            form={form}
            isEditable={isEditable}
            rules={[formRequiredRules(false, null, false), { type: 'email', message: '이메일을 확인해주세요!' }]}
            initialValue={editableUserInfo.email}
            name="email"
          />

          {isNil(userDetailInfo.gender) && !isEditable ? (
            <BaseForm className="account-user-detail-base-form" label="성별" content={editableUserInfo.gender} />
          ) : (
            <SelectForm
              isEditable={isEditable}
              className="user-detail-select-form"
              label="성별"
              name="gender"
              form={form}
              initialValue={editableUserInfo.gender}
              list={[
                {
                  id: ACCOUNT_USER_GENDER_TYPE.NOT_EXISTED,
                  name: ACCOUNT_USER_GENDER_TYPE_TEXT[ACCOUNT_USER_GENDER_TYPE['NOT_EXISTED']],
                },
                {
                  id: ACCOUNT_USER_GENDER_TYPE.FEMALE,
                  name: ACCOUNT_USER_GENDER_TYPE_TEXT[ACCOUNT_USER_GENDER_TYPE['FEMALE']],
                },
                {
                  id: ACCOUNT_USER_GENDER_TYPE.MALE,
                  name: ACCOUNT_USER_GENDER_TYPE_TEXT[ACCOUNT_USER_GENDER_TYPE['MALE']],
                },
              ]}
            />
          )}
        </div>

        <div className={cx('form-wrap', { editable: isEditable })}>
          {isNil(userDetailInfo.status) && !isEditable ? (
            <BaseForm className="account-user-detail-base-form" label="계정 상태" content={editableUserInfo.status} />
          ) : (
            <SelectForm
              isEditable={isEditable}
              className="user-detail-select-form"
              label="계정 상태"
              name="status"
              form={form}
              rules={[formRequiredRules(true, REQUIRED_MSG, false)]}
              initialValue={editableUserInfo.status}
              list={[
                {
                  id: ACCOUNT_USER_DETAIL_STATUS.ACTIVATE,
                  name: ACCOUNT_USER_DETAIL_STATUS_TEXT[ACCOUNT_USER_DETAIL_STATUS.ACTIVATE],
                },
                {
                  id: ACCOUNT_USER_DETAIL_STATUS.INACTIVE,
                  name: ACCOUNT_USER_DETAIL_STATUS_TEXT[ACCOUNT_USER_DETAIL_STATUS.INACTIVE],
                },
                {
                  id: ACCOUNT_USER_DETAIL_STATUS.DELETE_FROM_USER,
                  name: ACCOUNT_USER_DETAIL_STATUS_TEXT[ACCOUNT_USER_DETAIL_STATUS.DELETE_FROM_USER],
                },
              ]}
            />
          )}

          {!isEditable ? (
            <BaseForm
              className="account-user-detail-base-form"
              label="생년월일"
              content={setDateFormat(editableUserInfo.birthday)}
            />
          ) : (
            <DateSingleForm
              className="user-detail-date-form"
              value={editableUserInfo.birthday}
              label="생년월일"
              name="birthday"
              form={form}
              outFormat={DATE_FORMAT.YMD_DASH_FORMAT}
              btnTextFormat={DATE_PICKER_DEFAULT_PLACEHOLDER_TEXT}
              format={DATE_FORMAT.YMD_KOREAN_FORMAT}
              onSelect={(value: EditableUserDetailType['birthday']) => {
                handleInputForm('birthday', value);
              }}
              allowClear={true}
              required={false}
            />
          )}
        </div>
      </div>
      <div className="account-user-detail-section-content">
        <div className="form-wrap">
          {isNil(userDetailInfo.emailReceive) && !isEditable ? (
            <BaseForm
              className="account-user-detail-base-form"
              label="이메일 수신동의"
              content={editableUserInfo.emailReceive}
            />
          ) : (
            <SelectForm
              isEditable={isEditable}
              className="user-detail-select-form"
              label="이메일 수신동의"
              name="emailReceive"
              form={form}
              rules={[formRequiredRules(true, REQUIRED_MSG, false)]}
              initialValue={editableUserInfo.emailReceive}
              list={[
                {
                  id: ACCOUNT_USER_RECEIVE_TYPE.REJECT,
                  name: '미동의',
                },
                {
                  id: ACCOUNT_USER_RECEIVE_TYPE.RECEIVE,
                  name: '동의',
                },
              ]}
            />
          )}
          {(isNil(userDetailInfo.refundExtra?.bankId) ||
            userDetailInfo.refundExtra?.bankId === ACCOUNT_USER_BANK_ID.NULL) &&
          !isEditable ? (
            <BaseForm
              className="account-user-detail-base-form"
              label="환불 계좌 은행"
              content={editableUserInfo.bankId}
            />
          ) : (
            <SelectForm
              isEditable={isEditable}
              className="user-detail-select-form"
              label="환불 계좌 은행"
              form={form}
              rules={[formRequiredRules(false, null, false)]}
              initialValue={editableUserInfo.bankId}
              onChange={(value: EditableUserDetailType['bankId']) => {
                handleInputForm('bankId', value);
              }}
              list={ACCOUNT_USER_BANK_LIST}
              name="bankId"
            />
          )}
        </div>

        <div className={cx('form-wrap', { editable: isEditable })}>
          {isNil(userDetailInfo.smsReceive) && !isEditable ? (
            <BaseForm
              className="account-user-detail-base-form"
              label="SMS 수신동의"
              content={editableUserInfo.smsReceive}
            />
          ) : (
            <SelectForm
              isEditable={isEditable}
              className="user-detail-select-form"
              label="SMS 수신동의"
              name="smsReceive"
              form={form}
              rules={[formRequiredRules(true, REQUIRED_MSG, false)]}
              initialValue={editableUserInfo.smsReceive}
              list={[
                {
                  id: ACCOUNT_USER_RECEIVE_TYPE.REJECT,
                  name: '미동의',
                },
                {
                  id: ACCOUNT_USER_RECEIVE_TYPE.RECEIVE,
                  name: '동의',
                },
              ]}
            />
          )}

          <InputForm
            className="user-detail-input-form"
            label="환불 계좌 예금주"
            form={form}
            disabled={!form.getFieldValue('bankId')}
            isEditable={isEditable}
            rules={[
              form.getFieldValue('bankId')
                ? formRequiredRules(true, '필수사항 입니다.')
                : formRequiredRules(false, null, false),
              {
                pattern: REX_ACCOUNT_HOLDER_EXCEPT_BRACKET,
                message: '예금주명은 한글, 영문, (),.! 만 입력 가능합니다.',
              },
            ]}
            initialValue={editableUserInfo.bankHolder}
            maxLength={MAXIMUM_NUMBER_OF_BALANCE_ACCOUNT_ACCOUNT_HOLDER_TEXT}
            name="bankHolder"
          />
        </div>
        <div className="form-wrap">
          <InputForm
            className="user-detail-input-form"
            label="친구초대 코드"
            form={form}
            isEditable={isEditable}
            rules={[
              formRequiredRules(false, null, false),
              { pattern: REX_INVITE_FRIEND_CODE, message: '친구초대 코드를 확인해주세요!' },
            ]}
            initialValue={editableUserInfo.referralCode}
            name="referralCode"
          />
          <InputForm
            className="user-detail-input-form"
            label="환불 계좌 번호"
            disabled={!form.getFieldValue('bankId')}
            form={form}
            isEditable={isEditable}
            rules={[
              form.getFieldValue('bankId')
                ? formRequiredRules(true, '필수사항 입니다.')
                : formRequiredRules(false, null, false),
              { pattern: REX_ACCOUNT_NUMBER, message: '계좌번호를 확인해주세요.' },
            ]}
            initialValue={editableUserInfo.bankNumber}
            name="bankNumber"
          />
        </div>
      </div>
      {/* 유저 편집 모달(메모 기능) */}
      <EditAccountUserStatusModal
        form={form}
        isOpen={isOpenEditAccountUserStatusModal}
        onSubmitEditDetailInfo={handleSave}
        onCloseModal={handleCloseAccountUserStatusModal}
      />
    </div>
  );
};

export default Form.create<IUserInfoEditSectionProps>()(UserInfoEditSection);
